import { useState, useRef, useEffect } from "react";
import { Layout } from "@/components/Layout";
import { Bot, Send, Loader2, Sparkles, Trash2, Mic, User, HelpCircle, LayoutDashboard, TrendingUp, Coins, MessageCircle, Snowflake } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useChat } from "@/lib/api/hooks/useChat";
import { authService } from "@/lib/api/services/auth.service";
import { cn } from "@/lib/utils";

// Speech Recognition API interfaces
interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
}

interface SpeechRecognitionConstructor {
  new (): SpeechRecognition;
}

// Extend Window interface for Speech Recognition API
declare global {
  interface Window {
    SpeechRecognition: SpeechRecognitionConstructor;
    webkitSpeechRecognition: SpeechRecognitionConstructor;
  }
}

interface SpeechRecognitionEvent {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}

interface SpeechRecognitionErrorEvent {
  error: string;
  message: string;
}

interface SpeechRecognitionResultList {
  length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
  length: number;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
  isFinal: boolean;
}

interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

const formatChatGPTResponse = (content: string): string => {
  if (!content) return content;
  
  let formatted = content;

  // Replace markdown bold markers (**text**) with placeholder for later processing
  formatted = formatted.replace(/\*\*([^*]+)\*\*/g, '{{BOLD_START}}$1{{BOLD_END}}');

  // Remove any remaining single asterisks (simple approach - remove all single *)
  // This handles cases where asterisks are used as bullet points or decoration
  formatted = formatted.replace(/\s*\*\s*/g, ' ');
  formatted = formatted.replace(/\*\s*/g, '');
  formatted = formatted.replace(/\s*\*/g, '');

  // Format currency (convert $ to ₹ if needed, or ensure ₹ is used)
  formatted = formatted.replace(/\$\s*(\d+(?:,\d{3})*(?:\.\d{2})?)/g, '₹$1');
  
  // Ensure Rupees format is consistent - handle various formats
  formatted = formatted.replace(/(\d+(?:,\d{3})*(?:\.\d{2})?)\s*(?:rupees|Rs\.|Rs|INR|rupee)/gi, '₹$1');

  // Format large numbers with commas (but not dates or times)
  formatted = formatted.replace(/\b(\d{4,})\b/g, (match, num) => {
    // Don't format if it looks like a year (1900-2100) or time
    if (parseInt(num) >= 1900 && parseInt(num) <= 2100) {
      return match;
    }
    try {
      return parseInt(num).toLocaleString('en-IN');
    } catch {
      return match;
    }
  });
     // REMOVE lines that are just separators like '---' or '------'
  formatted = formatted
  .split('\n')
  .filter(line => !/^\s*-{3,}\s*$/.test(line))
  .join('\n');
  // Clean up excessive line breaks (more than 2 consecutive)
  formatted = formatted.replace(/\n{3,}/g, '\n\n');

  // Trim whitespace but preserve structure
  formatted = formatted.trim();

  return formatted;
};

const renderFormattedText = (text: string) => {
  if (!text) return null;
  
  // Handle bold placeholders {{BOLD_START}}text{{BOLD_END}}
  // Use a more robust regex to match all bold sections
  const parts = text.split(/({{BOLD_START}}[^{]+?{{BOLD_END}})/g);
  
  return parts.map((part, index) => {
    if (part.startsWith('{{BOLD_START}}') && part.endsWith('{{BOLD_END}}')) {
      const boldText = part.replace(/{{BOLD_START}}/g, '').replace(/{{BOLD_END}}/g, '');
      return (
        <strong key={`bold-${index}`} className="font-semibold text-white/95">
          {boldText}
        </strong>
      );
    }
    if (part) {
      return <span key={`text-${index}`}>{part}</span>;
    }
    return null;
  }).filter(Boolean);
};

const renderMessageContent = (message: { role: string; content: string }) => {
  // Only format assistant messages (ChatGPT responses)
  if (message.role === "assistant") {
    const formattedContent = formatChatGPTResponse(message.content);
    
    // Split content by lines to handle bullet points and formatting
    const lines = formattedContent.split('\n');
    
    return (
      <div className="text-sm break-words leading-relaxed space-y-2">
        {lines.map((line, index) => {
          const trimmedLine = line.trim();
          
          // Skip empty lines but preserve spacing
          if (!trimmedLine) {
            return <div key={`empty-${index}`} className="h-1" />;
          }
          
          // Handle bullet points (various formats, but exclude asterisks)
          const bulletMatch = trimmedLine.match(/^[-•]\s+(.+)$/);
          if (bulletMatch) {
            return (
              <div key={`bullet-${index}`} className="flex items-start gap-2.5">
                <span className="text-white/80 mt-0.5 flex-shrink-0 text-lg leading-none">•</span>
                <span className="flex-1">{renderFormattedText(bulletMatch[1])}</span>
              </div>
            );
          }
          
          // Handle lines that start with icon emojis or special characters followed by bold text
          // Pattern: emoji/icon {{BOLD_START}}Bold Label{{BOLD_END}}: value
          if (trimmedLine.includes('{{BOLD_START}}') && trimmedLine.includes('{{BOLD_END}}')) {
            const iconBoldMatch = trimmedLine.match(/^([^\w\s]*)\s*{{BOLD_START}}(.+?){{BOLD_END}}:\s*(.+)$/);
            if (iconBoldMatch) {
              return (
                <div key={`icon-bold-${index}`} className="flex items-start gap-2.5">
                  {iconBoldMatch[1] && (
                    <span className="text-white/90 mt-0.5 flex-shrink-0 text-base">
                      {iconBoldMatch[1]}
                    </span>
                  )}
                  <div className="flex-1">
                    <strong className="font-semibold text-white/95">{iconBoldMatch[2]}:</strong>{' '}
                    <span>{renderFormattedText(iconBoldMatch[3])}</span>
                  </div>
                </div>
              );
            }
          }
          
          // Handle numbered lists (1. or 1) format)
          const numberedMatch = trimmedLine.match(/^(\d+)[.)]\s+(.+)$/);
          if (numberedMatch) {
            return (
              <div key={`numbered-${index}`} className="flex items-start gap-2.5">
                <span className="text-white/80 font-semibold mt-0.5 flex-shrink-0 min-w-[1.5rem]">
                  {numberedMatch[1]}.
                </span>
                <span className="flex-1">{renderFormattedText(numberedMatch[2])}</span>
              </div>
            );
          }
          
          // Regular paragraph line with formatted text
          return (
            <div key={`para-${index}`} className="pl-1">
              {renderFormattedText(trimmedLine)}
            </div>
          );
        })}
      </div>
    );
  }
  
  // User messages - simple display
  return (
    <span className="text-sm whitespace-pre-wrap break-words leading-relaxed">
      {message.content}
    </span>
  );
};

export default function AIAssistant() {
  const [inputValue, setInputValue] = useState("");
  const [isListening, setIsListening] = useState(false);
  const [showQuickQuestions, setShowQuickQuestions] = useState(false);
  const [showRelevantQuestions, setShowRelevantQuestions] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const { messages, sendMessage, clearMessages, isLoading } = useChat();
  
  // Get relevant questions from the last assistant message
  const lastAssistantMessage = messages.filter(m => m.role === "assistant").pop();
  const relevantQuestions = lastAssistantMessage?.relevantQuestions || [];

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesContainerRef.current) {
      // Use setTimeout to ensure DOM has fully updated
      const timeoutId = setTimeout(() => {
        if (messagesContainerRef.current) {
          // Scroll within the messages container, not the entire page
          const container = messagesContainerRef.current;
          container.scrollTo({
            top: container.scrollHeight,
            behavior: "smooth"
          });
        }
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [messages, isLoading]);

  // Focus input on mount
  useEffect(() => {
    if (inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, []);

  // Initialize Speech Recognition API
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (SpeechRecognition) {
      const recognition = new SpeechRecognition() as SpeechRecognition;
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = "en-US";

      recognition.onresult = (event: SpeechRecognitionEvent) => {
        const transcript = event.results[0][0].transcript;
        setInputValue((prev) => prev + (prev ? " " : "") + transcript);
        setIsListening(false);
      };

      recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
        console.error("Speech recognition error:", event.error);
        setIsListening(false);
      };

      recognition.onend = () => {
        setIsListening(false);
      };

      recognitionRef.current = recognition;
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
        recognitionRef.current = null;
      }
    };
  }, []);

  const handleMicClick = () => {
    if (!recognitionRef.current) {
      console.warn("Speech recognition not supported in this browser");
      return;
    }

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      try {
        recognitionRef.current.start();
        setIsListening(true);
      } catch (error) {
        console.error("Error starting speech recognition:", error);
        setIsListening(false);
      }
    }
  };

  const isSpeechRecognitionSupported = () => {
    return !!(window.SpeechRecognition || window.webkitSpeechRecognition);
  };

  const handleSend = () => {
    if (!inputValue.trim() || isLoading) return;
    
    const message = inputValue.trim();
    setInputValue("");
    sendMessage(message);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Quick Questions data
  const quickQuestions = {
    dashboard: [
      "Give me a performance overview of my business dashboard.",
      "Highlight key KPIs where my business is underperforming.",
      "Show improvement opportunities across sales, inventory, and collections.",
      "Identify risks in stock, expiry, and cash flow that affect performance.",
      "Suggest actions to improve overall business efficiency and profitability."
    ],
    sales: [
      "How can I increase my sales growth and overall performance?",
      "Which products or customers are driving the best sales performance?",
      "Show me sales trends and areas where I am underperforming.",
      "What actions can I take to improve monthly and year-on-year sales?",
      "Suggest strategies to improve conversion, repeat sales, and upselling."
    ],
    outstanding: [
      "How can I improve my collections performance and reduce outstanding?",
      "Show me customers with poor payment performance and high risk.",
      "Identify aging buckets that need immediate follow-up to improve cash flow.",
      "What collection strategies can I follow to improve on-time payments?",
      "Highlight key KPIs to monitor and improve receivables performance."
    ]
  };

  const handleQuestionClick = (question: string) => {
    if (!isLoading && question.trim()) {
      setShowQuickQuestions(false);
      sendMessage(question.trim());
    }
  };

  const handleRelevantQuestionClick = (question: string) => {
    if (!isLoading && question.trim()) {
      sendMessage(question.trim());
    }
  };

  return (
    <Layout>
      {/* Floating Clear Chat Button - Top Left Corner (like chatbot icon) */}
      {messages.length > 0 && (
        <Button
          variant="outline"
          size="sm"
          onClick={clearMessages}
          className="fixed top-20 left-6 ml-8 z-50 gap-2 shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 transition-all duration-300 bg-background/95 backdrop-blur-sm"
        >
          <Trash2 className="w-4 h-4" />
          Clear Chat
        </Button>
      )}
      <div className="flex flex-col h-full min-h-0 overflow-hidden">
        {/* Chat Container */}
        <Card className="flex-1 flex flex-col overflow-hidden border-white/10 dark:border-white/5 bg-gradient-to-br from-background/95 via-background/90 to-muted/50 backdrop-blur-xl min-h-0">
          <CardContent className="flex-1 flex flex-col p-0 overflow-hidden min-h-0">
            {/* Messages Area */}
            <div
              ref={messagesContainerRef}
              className="flex-1 overflow-y-auto p-6 space-y-4 min-h-0 overscroll-contain flex-shrink"
              style={{ overscrollBehavior: 'contain' }}
            >

              {messages.length === 0 ? (
                <div className="flex flex-col lg:flex-row items-center justify-center h-full gap-8 lg:gap-12 px-4 py-8 relative overflow-hidden">
                  {/* Animated gradient background */}
                  <div className="absolute inset-0 opacity-5 pointer-events-none">
                    <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-gradient-to-r from-primary to-accent blur-3xl" />
                    <div className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full bg-gradient-to-r from-accent to-primary blur-3xl" />
                  </div>

                  {/* Premium Illustrations - Floating Geometric Shapes */}
                  <div className="absolute inset-0 pointer-events-none overflow-hidden">
                    {/* Large floating circles */}
                    <div className="absolute top-10 left-10 w-32 h-32 rounded-full bg-gradient-to-br from-primary/20 to-accent/10 blur-2xl animate-[float_8s_ease-in-out_infinite]" />
                    <div className="absolute bottom-20 right-20 w-40 h-40 rounded-full bg-gradient-to-br from-accent/20 to-primary/10 blur-2xl animate-[float_10s_ease-in-out_infinite_reverse]" />
                    
                    {/* Hexagon illustrations */}
                    <svg className="absolute top-20 right-32 w-24 h-24 opacity-20 animate-[float_12s_ease-in-out_infinite]" viewBox="0 0 100 100">
                      <polygon points="50,5 90,25 90,75 50,95 10,75 10,25" fill="none" stroke="url(#hexGradient1)" strokeWidth="2" />
                      <defs>
                        <linearGradient id="hexGradient1" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="hsl(var(--primary))" />
                          <stop offset="100%" stopColor="hsl(var(--accent))" />
                        </linearGradient>
                      </defs>
                    </svg>
                    
                    <svg className="absolute bottom-32 left-24 w-20 h-20 opacity-15 animate-[float_14s_ease-in-out_infinite_reverse]" viewBox="0 0 100 100">
                      <polygon points="50,5 90,25 90,75 50,95 10,75 10,25" fill="url(#hexGradient2)" opacity="0.3" />
                      <defs>
                        <linearGradient id="hexGradient2" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="hsl(var(--accent))" />
                          <stop offset="100%" stopColor="hsl(var(--primary))" />
                        </linearGradient>
                      </defs>
                    </svg>

                    {/* Abstract data visualization elements */}
                    <div className="absolute top-1/3 right-1/4 flex flex-col gap-2 opacity-10">
                      <div className="h-8 w-2 bg-gradient-to-t from-primary to-accent rounded-full animate-[grow_2s_ease-in-out_infinite]" />
                      <div className="h-12 w-2 bg-gradient-to-t from-accent to-primary rounded-full animate-[grow_2.5s_ease-in-out_infinite]" style={{ animationDelay: '0.2s' }} />
                      <div className="h-6 w-2 bg-gradient-to-t from-primary to-accent rounded-full animate-[grow_2.2s_ease-in-out_infinite]" style={{ animationDelay: '0.4s' }} />
                    </div>

                    <div className="absolute bottom-1/3 left-1/4 flex flex-row gap-2 opacity-10">
                      <div className="w-8 h-2 bg-gradient-to-r from-primary to-accent rounded-full animate-[grow_2.3s_ease-in-out_infinite]" />
                      <div className="w-12 h-2 bg-gradient-to-r from-accent to-primary rounded-full animate-[grow_2.7s_ease-in-out_infinite]" style={{ animationDelay: '0.3s' }} />
                      <div className="w-6 h-2 bg-gradient-to-r from-primary to-accent rounded-full animate-[grow_2.4s_ease-in-out_infinite]" style={{ animationDelay: '0.6s' }} />
                    </div>

                    {/* Sparkle/star decorations */}
                    <div className="absolute top-1/2 left-1/3 w-1 h-1 bg-primary rounded-full opacity-60 animate-[sparkle_3s_ease-in-out_infinite]" />
                    <div className="absolute top-1/4 right-1/3 w-1.5 h-1.5 bg-accent rounded-full opacity-50 animate-[sparkle_3.5s_ease-in-out_infinite]" style={{ animationDelay: '0.5s' }} />
                    <div className="absolute bottom-1/4 left-1/2 w-1 h-1 bg-primary rounded-full opacity-55 animate-[sparkle_4s_ease-in-out_infinite]" style={{ animationDelay: '1s' }} />
                    <div className="absolute top-3/4 right-1/2 w-1.5 h-1.5 bg-accent rounded-full opacity-45 animate-[sparkle_3.2s_ease-in-out_infinite]" style={{ animationDelay: '1.5s' }} />

                    {/* Grid pattern overlay */}
                    <svg className="absolute inset-0 w-full h-full opacity-[0.02]" xmlns="http://www.w3.org/2000/svg">
                      <defs>
                        <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                          <path d="M 40 0 L 0 0 0 40" fill="none" stroke="hsl(var(--primary))" strokeWidth="1"/>
                        </pattern>
                      </defs>
                      <rect width="100%" height="100%" fill="url(#grid)" />
                    </svg>

                    {/* Curved lines illustration */}
                    <svg className="absolute top-0 right-0 w-64 h-64 opacity-5" viewBox="0 0 200 200">
                      <path d="M 20 100 Q 50 50, 100 100 T 180 100" fill="none" stroke="hsl(var(--primary))" strokeWidth="2" />
                      <path d="M 20 120 Q 50 70, 100 120 T 180 120" fill="none" stroke="hsl(var(--accent))" strokeWidth="2" />
                    </svg>

                    <svg className="absolute bottom-0 left-0 w-64 h-64 opacity-5" viewBox="0 0 200 200">
                      <path d="M 180 100 Q 150 150, 100 100 T 20 100" fill="none" stroke="hsl(var(--accent))" strokeWidth="2" />
                      <path d="M 180 80 Q 150 30, 100 80 T 20 80" fill="none" stroke="hsl(var(--primary))" strokeWidth="2" />
                    </svg>
                  </div>
                  
                  {/* Left Side - Icon and Welcome Text */}
                  <div className="flex flex-col items-center text-center w-full lg:w-auto lg:flex-1 max-w-md lg:max-w-none">
                    {/* Icon with premium glow + motion */}
                    <div className="relative mb-6 ml-8 lg:ml-12 group flex items-center justify-center w-28 h-28 sm:w-32 sm:h-32">
                      {/* Soft animated glow behind the icon */}
                      <div className="pointer-events-none absolute -inset-6 rounded-[32px] bg-gradient-to-br from-primary/25 via-accent/20 to-primary/10 blur-3xl opacity-70 animate-[iconGlow_10s_ease-in-out_infinite]" />

                      {/* Floating AI text icon */}
                      <span className="text-6xl sm:text-7xl font-extrabold tracking-tight shiny-text-gradient drop-shadow-[0_8px_24px_rgba(15,23,42,0.55)]">
                        AI
                      </span>

                      {/* Small premium detail orbs */}
                      <div className="pointer-events-none absolute inset-0">
                        <span className="absolute -right-1 top-5 h-2 w-2 rounded-full bg-primary/80 shadow-[0_0_18px_rgba(59,130,246,0.85)] animate-[iconOrbit_14s_ease-in-out_infinite]" />
                        <span className="absolute left-1 -bottom-1 h-1.5 w-1.5 rounded-full bg-accent/80 shadow-[0_0_16px_rgba(45,212,191,0.85)] animate-[iconOrbit_16s_ease-in-out_infinite_reverse]" />
                      </div>
                    </div>  
                    
                    <div className="space-y-2">
                      <h3 className="text-2xl sm:text-3xl font-semibold tracking-tight shiny-text-gradient">
                        Welcome to PharmaSoft CS Mithra
                      </h3>
                      <p className="text-muted-foreground leading-relaxed">
                        I'm here to help you about your Dashboards, Sales and Outstandings. 
                       
                      </p>
                      <p className="text-muted-foreground leading-relaxed">
                        Start a conversation by typing a message below.
                      </p>
                    </div>
                  </div>
                  
                  {/* Right Side - Example prompts */}
                  <div className="flex flex-col gap-3 w-full lg:w-auto lg:flex-1 max-w-md lg:max-w-none">
                    {[
                      "Give me a quick summary of my sales dashboard.",
                      "List the top 5 customers by sales this month.",
                      "Suggest 3 actions to grow sales next quarter.",
                      "List customers with overdue invoices and their outstanding amounts.",
                      "Give me a plan to boost revenue growth and improve collections."
                    ].map((prompt, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          if (!isLoading && prompt.trim()) {
                            sendMessage(prompt.trim());
                          }
                        }}
                        disabled={isLoading}
                        className="group relative overflow-hidden rounded-lg p-4 text-left bg-gradient-to-br from-background/80 to-muted/30 backdrop-blur-sm border border-white/10 dark:border-white/5 hover:border-primary/30 transition-all duration-300 hover:shadow-md hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <div className="flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-primary opacity-50 group-hover:opacity-100 transition-opacity" />
                          <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                            {prompt}
                          </span>
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-primary to-accent opacity-0 group-hover:opacity-100 transition-opacity" />
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={cn(
                        "flex w-full animate-in fade-in slide-in-from-bottom-2 duration-300",
                        message.role === "user" ? "justify-end" : "justify-start"
                      )}
                    >
                      <div className="flex items-start gap-3 max-w-[80%] md:max-w-[70%]">
                        {message.role === "assistant" && (
                          <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center shadow-lg shadow-primary/25">
                            <span className="text-xs font-semibold text-white">AI</span>
                          </div>
                        )}
                        <div
                          className={cn(
                            "rounded-2xl px-4 py-3 shadow-lg",
                            "bg-gradient-to-br from-primary via-primary/90 to-accent text-white"
                          )}
                        >
                          <div>
                            {renderMessageContent(message)}
                          </div>
                          <span className="text-xs mt-2 block opacity-70 text-white/80">
                            {new Date(message.timestamp).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                        </div>
                        {message.role === "user" && (
                          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center border border-primary/30">
                            {authService.getCurrentUser()?.userName ? (
                              <span className="text-xs font-semibold text-primary">
                                {authService.getCurrentUser()?.userName?.charAt(0).toUpperCase()}
                              </span>
                            ) : (
                              <User className="w-4 h-4 text-primary" />
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {isLoading && (
                    <div className="flex w-full animate-in fade-in slide-in-from-bottom-2 duration-300 justify-start">
                      <div className="flex items-start gap-3 max-w-[80%] md:max-w-[70%]">
                        <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center shadow-lg shadow-primary/25">
                          <span className="text-xs font-semibold text-white">AI</span>
                        </div>
                        <div className="bg-gradient-to-br from-primary via-primary/90 to-accent text-white rounded-2xl px-4 py-3 shadow-lg">
                          <div className="flex items-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin text-white" />
                            <span className="text-sm text-white/90">Mithra Analysing...</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {/* Input Area */}
            <div className="flex-shrink-0 border-t border-border bg-muted/60 backdrop-blur-sm px-6 py-2">
              {/* Relevant Questions above input field */}
              {relevantQuestions.length > 0 && !isLoading && showRelevantQuestions && (
                <div className="mb-3 w-full flex items-start gap-2">
                  {/* Ice/toggle icon */}
                  <button
                    type="button"
                    onClick={() => setShowRelevantQuestions(false)}
                    className="mt-0.5 flex items-center justify-center w-7 h-7 rounded-full border border-border bg-background/80 hover:bg-muted text-muted-foreground hover:text-primary transition-colors duration-200 shadow-sm"
                    title="Hide suggestions"
                  >
                    <Snowflake className="w-3.5 h-3.5" />
                  </button>

                  <div className="flex-1">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {relevantQuestions.map((q, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleRelevantQuestionClick(q)}
                          disabled={isLoading}
                          className="text-xs px-3 py-1.5 rounded-lg bg-background/80 hover:bg-background border border-border hover:border-primary/50 text-foreground hover:text-primary transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow text-left"
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              <div className="flex gap-3 max-w-4xl mx-auto">
                {/* Ice icon inline with input when suggestions are hidden */}
                {relevantQuestions.length > 0 && !isLoading && !showRelevantQuestions && (
                  <button
                    type="button"
                    onClick={() => setShowRelevantQuestions(true)}
                    className="mt-1 flex items-center justify-center w-8 h-8 rounded-full border border-border bg-background/80 hover:bg-muted text-muted-foreground hover:text-primary transition-colors duration-200 shadow-sm"
                    title="Show suggestions"
                  >
                    <Snowflake className="w-4 h-4" />
                  </button>
                )}
                <div className="flex-1 relative">
                  <div className="relative flex items-center">
                    <Input
                      ref={inputRef}
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Type your message..."
                      disabled={isLoading}
                      className="pr-11 h-11 rounded-lg border border-border bg-card text-foreground placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-all"
                    />
                    <button
                      type="button"
                      onClick={handleMicClick}
                      disabled={!isSpeechRecognitionSupported() || isLoading}
                      className={cn(
                        "absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-md transition-all z-10",
                        isListening
                          ? "text-primary bg-primary/10"
                          : "text-muted-foreground hover:text-foreground hover:bg-muted",
                        (!isSpeechRecognitionSupported() || isLoading) && "opacity-40 cursor-not-allowed"
                      )}
                      title={isSpeechRecognitionSupported() ? "Click to start voice input" : "Speech recognition not supported"}
                    >
                      <Mic className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <Button
                  onClick={handleSend}
                  disabled={!inputValue.trim() || isLoading}
                  className="h-11 px-6 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm hover:shadow transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                  size="default"
                >
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Send
                    </>
                  )}
                </Button>
              </div>
              {/*
              <p className="text-xs text-muted-foreground text-center mt-1.5">
                Press Enter to send, Shift+Enter for new line
              </p>
              */}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Questions Floating Action Button - Bottom Right */}
      <Button
        onClick={() => setShowQuickQuestions(true)}
        className="fixed bottom-24 right-6 z-[100] h-10 w-10 rounded-full bg-gradient-to-br from-primary to-accent text-white shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 transition-all duration-300 hover:scale-110 flex items-center justify-center"
        size="icon"
        title="Quick Questions"
      >
        <MessageCircle className="w-4 h-4" />
      </Button>

      {/* Quick Questions Sheet */}
      <Sheet open={showQuickQuestions} onOpenChange={setShowQuickQuestions}>
        <SheetContent side="left" className="w-[400px] sm:w-[450px] p-0">
          <SheetHeader className="p-6 pb-4 border-b border-border">
            <SheetTitle className="text-xl font-semibold">Quick Questions</SheetTitle>
          </SheetHeader>
          
          <div className="p-6">
            <Tabs defaultValue="dashboard" className="w-full">
              <TabsList className="grid w-full grid-cols-3 mb-6 bg-muted/50">
                <TabsTrigger 
                  value="dashboard" 
                  className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                >
                  <LayoutDashboard className="w-4 h-4 flex-shrink-0" />
                  <span className="hidden sm:inline">Dashboard</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="sales"
                  className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                >
                  <TrendingUp className="w-4 h-4 flex-shrink-0" />
                  <span className="hidden sm:inline">Sales</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="outstanding"
                  className="flex items-center gap-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                >
                  <Coins className="w-4 h-4 flex-shrink-0" />
                  <span className="hidden sm:inline">Outstanding</span>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="dashboard" className="mt-0">
                <div className="space-y-2">
                  {quickQuestions.dashboard.map((question, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleQuestionClick(question)}
                      disabled={isLoading}
                      className="w-full group relative overflow-hidden rounded-lg p-4 text-left bg-gradient-to-br from-background/80 to-muted/30 backdrop-blur-sm border border-white/10 dark:border-white/5 hover:border-primary/30 transition-all duration-300 hover:shadow-md hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-primary opacity-50 group-hover:opacity-100 transition-opacity" />
                        <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                          {question}
                        </span>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-primary to-accent opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="sales" className="mt-0">
                <div className="space-y-2">
                  {quickQuestions.sales.map((question, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleQuestionClick(question)}
                      disabled={isLoading}
                      className="w-full group relative overflow-hidden rounded-lg p-4 text-left bg-gradient-to-br from-background/80 to-muted/30 backdrop-blur-sm border border-white/10 dark:border-white/5 hover:border-primary/30 transition-all duration-300 hover:shadow-md hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-primary opacity-50 group-hover:opacity-100 transition-opacity" />
                        <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                          {question}
                        </span>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-primary to-accent opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="outstanding" className="mt-0">
                <div className="space-y-2">
                  {quickQuestions.outstanding.map((question, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleQuestionClick(question)}
                      disabled={isLoading}
                      className="w-full group relative overflow-hidden rounded-lg p-4 text-left bg-gradient-to-br from-background/80 to-muted/30 backdrop-blur-sm border border-white/10 dark:border-white/5 hover:border-primary/30 transition-all duration-300 hover:shadow-md hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-primary opacity-50 group-hover:opacity-100 transition-opacity" />
                        <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                          {question}
                        </span>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-primary to-accent opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </SheetContent>
      </Sheet>

      {/* Listening Dialog */}
      <Dialog open={isListening} onOpenChange={(open) => !open && setIsListening(false)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center">Listening...</DialogTitle>
            <DialogDescription className="text-center text-base pt-2">
              Speak now
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center py-4">
            <div className="relative">
              <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center">
                <Mic className="w-10 h-10 text-primary animate-pulse" />
              </div>
              <div className="absolute inset-0 w-20 h-20 rounded-full bg-primary/10 animate-ping" />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

